import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CloseOutlined, Replay } from '@mui/icons-material';

import { useConfirm } from '@components/common/confirm/confirm.context';
import { useSystemAlert } from '@components/common/systemAlert/systemAlert.context';

import { RefundForm } from '@models/order';
import { OrderResponse, refundOrder } from '@apis/order';

type FullRefundEsimContentProps = {
  onClose: (refresh: boolean) => void;
  order: OrderResponse;
  totalCount: number;
};

type SummaryFieldProps = {
  label: string;
  value: React.ReactNode;
};

type SummaryGroupProps = {
  title: string;
  children: React.ReactNode;
};

type CalculationRowProps = {
  label: string;
  value: React.ReactNode;
  helperText?: React.ReactNode;
  input?: React.ReactNode;
  strong?: boolean;
};

type CalculationGroupProps = {
  title: string;
  children: React.ReactNode;
  accent?: boolean;
};

const formatUsd = (value: number) => `$${value.toFixed(2)}`;

const parseMoneyInput = (value: string) => {
  if (!/(?!0\d)^[0-9]*\.?[0-9]*$/.test(value)) return null;
  return Math.round(Number(value || 0) * 100) / 100;
};

const SummaryField = ({ label, value }: SummaryFieldProps) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '130px minmax(0, 1fr)' },
      gap: { xs: 0.35, sm: 1.5 },
      alignItems: 'baseline',
    }}
  >
    <Typography variant="caption" fontWeight={800} color="text.secondary">
      {label}
    </Typography>
    <Typography
      variant="body2"
      fontWeight={700}
      color="text.primary"
      sx={{ overflowWrap: 'anywhere', minWidth: 0 }}
    >
      {value ?? '-'}
    </Typography>
  </Box>
);

const SummaryGroup = ({ title, children }: SummaryGroupProps) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      p: 1.5,
      bgcolor: 'background.default',
    }}
  >
    <Typography variant="subtitle2" fontWeight={900} sx={{ mb: 1.25 }}>
      {title}
    </Typography>
    <Stack spacing={1.15}>{children}</Stack>
  </Box>
);

const CalculationGroup = ({ title, children, accent = false }: CalculationGroupProps) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: accent ? 'primary.main' : 'divider',
      borderRadius: 1,
      p: 1.5,
      bgcolor: accent ? 'rgba(25, 118, 210, 0.06)' : 'rgba(0, 0, 0, 0.015)',
    }}
  >
    <Typography
      variant="subtitle2"
      fontWeight={900}
      color={accent ? 'primary.main' : 'text.primary'}
      sx={{ mb: 1.25 }}
    >
      {title}
    </Typography>

    <Stack spacing={1.15}>{children}</Stack>
  </Box>
);

const CalculationRow = ({ label, value, helperText, input, strong = false }: CalculationRowProps) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'minmax(150px, 1fr) minmax(0, 1fr)' },
      gap: { xs: 0.5, sm: 1.5 },
      alignItems: 'center',
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" fontWeight={700} color="text.secondary">
        {label}
      </Typography>
      {helperText ? (
        <Typography variant="caption" color="text.secondary">
          {helperText}
        </Typography>
      ) : null}
    </Box>

    {input ?? (
      <Typography
        variant={strong ? 'h6' : 'body2'}
        fontWeight={strong ? 900 : 700}
        color={strong ? 'primary.main' : 'text.primary'}
        sx={{
          minWidth: 0,
          textAlign: { xs: 'left', sm: 'right' },
          fontVariantNumeric: 'tabular-nums',
          overflowWrap: 'anywhere',
        }}
      >
        {value ?? '-'}
      </Typography>
    )}
  </Box>
);

const FullRefundEsimContent = ({
  onClose,
  order,
  totalCount,
}: FullRefundEsimContentProps) => {
  const confirm = useConfirm();
  const systemAlert = useSystemAlert();

  const [form, setForm] = useState<RefundForm>({
    refundFee: 0,
  });
  const [isSubmitting, setSubmitting] = useState(false);

  const remainingPaymentAmount = order.paymentAmount - order.refundAmount;
  const unitAmount = totalCount > 0 ? order.paymentAmount / totalCount : 0;
  const totalRefundFee = useMemo(
    () => form.refundFee * totalCount,
    [form.refundFee, totalCount],
  );
  const refundAmount = useMemo(
    () => Math.max(0, remainingPaymentAmount - totalRefundFee),
    [remainingPaymentAmount, totalRefundFee],
  );
  const canRefund = totalCount > 0 && refundAmount > 0 && !isSubmitting;

  const handleChangeRefundFee = (value: string) => {
    const next = parseMoneyInput(value);
    if (next === null) return;

    setForm((prev) => ({
      ...prev,
      refundFee: next,
    }));
  };

  const handleOnConfirm = async () => {
    if (isSubmitting) return;

    if (totalCount <= 0) {
      systemAlert.error('There are no order items available for refund.');
      return;
    }

    if (refundAmount <= 0) {
      systemAlert.error('Refund fee must be less than the remaining payment amount.');
      return;
    }

    const ok = await confirm({
      priority: 'danger',
      title: 'Refund the full order?',
      description: `A refund of ${formatUsd(refundAmount)} will be processed for ${totalCount.toLocaleString()} item(s).`,
      confirmText: 'Refund',
      cancelText: 'Cancel',
      autoFocusConfirm: false,
    });

    if (!ok) return;

    setSubmitting(true);
    try {
      await refundOrder(order.orderId, {
        esimType: order.stock.esimType,
        refundType: 'FULL_REFUND',
        esimIds: [],
        refundFee: form.refundFee,
      });
      systemAlert.success('Refund processed successfully.');
      onClose(true);
    } catch {
      systemAlert.error('Failed to process refund. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
        <Box>
          <Typography variant="h6" fontWeight={900}>
            Full Refund
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review the order and confirm the full refund amount.
          </Typography>
        </Box>

        <IconButton aria-label="Close full refund dialog" onClick={() => onClose(false)} size="small">
          <CloseOutlined fontSize="small" />
        </IconButton>
      </Box>

      <Divider />

      <SummaryGroup title="Order Summary">
        <SummaryField label="Order Code" value={String(order.orderCode)} />
        <SummaryField label="Product Name" value={order.product.productName} />
        <SummaryField label="Product Code" value={order.product.productCode} />
        <SummaryField label="Stock Code" value={order.stock.stockCode} />
      </SummaryGroup>

      <Divider />

      <Stack spacing={2}>
        <Typography variant="subtitle2" fontWeight={800}>
          Refund Calculation
        </Typography>

        <Stack spacing={1.5}>
          <CalculationGroup title="Refund Base">
            <CalculationRow label="Unit Amount" value={formatUsd(unitAmount)} />
            <CalculationRow label="Quantity" value={totalCount.toLocaleString()} />
            <CalculationRow label="Remaining Amount" value={formatUsd(remainingPaymentAmount)} />
          </CalculationGroup>

          <CalculationGroup title="Deduction">
            <CalculationRow
              label="Refund Fee per Item"
              value={formatUsd(form.refundFee)}
              input={
                <TextField
                  value={form.refundFee.toString()}
                  onChange={(e) => handleChangeRefundFee(e.target.value)}
                  size="small"
                  type="number"
                  fullWidth
                  inputProps={{ min: 0, step: 0.01, 'aria-label': 'Refund Fee per Item (USD)' }}
                  sx={{ maxWidth: { sm: 220 }, justifySelf: { sm: 'end' } }}
                />
              }
            />
            <CalculationRow
              label="Total Refund Fee"
              value={formatUsd(totalRefundFee)}
              helperText={`${formatUsd(form.refundFee)} x ${totalCount.toLocaleString()}`}
            />
          </CalculationGroup>

          <CalculationGroup title="Final Refund" accent>
            <CalculationRow label="Refund Amount" value={formatUsd(refundAmount)} strong />
          </CalculationGroup>
        </Stack>
      </Stack>

      <Box display="flex" justifyContent="flex-end" gap={1.5}>
        <Button variant="outlined" onClick={() => onClose(false)} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<Replay />}
          onClick={handleOnConfirm}
          disabled={!canRefund}
        >
          {isSubmitting ? 'Refunding...' : 'Refund'}
        </Button>
      </Box>
    </Stack>
  );
};

export default FullRefundEsimContent;
